import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Signup() {
  const nav = useNavigate();
  const { signup } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, name);
      const pending = localStorage.getItem("stb_pending_invite");
      if (pending) {
        localStorage.removeItem("stb_pending_invite");
        toast.success("Account created");
        nav(`/join/${pending}`);
      } else {
        toast.success("Account created");
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <div className="px-6 sm:px-12 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-3">/ Onboard</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mb-2">Start splitting</h1>
          <p className="text-white/60 mb-10">10 seconds to your first group.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Name</Label>
              <Input
                id="name" required value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 bg-black/40 border-white/10 focus-visible:ring-[var(--brand)]/50"
                placeholder="Jane Doe"
                data-testid="signup-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-black/40 border-white/10 focus-visible:ring-[var(--brand)]/50"
                placeholder="you@somewhere.com"
                data-testid="signup-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Password</Label>
              <PasswordInput
                id="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 bg-black/40 border-white/10 focus-visible:ring-[var(--brand)]/50"
                placeholder="at least 8 chars"
                data-testid="signup-password-input"
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full h-12 brand-bg text-black font-semibold hover:opacity-90 active:scale-[0.98] transition"
              data-testid="signup-submit-btn"
            >
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton label="Sign up with Google" testId="google-signup-btn" />
          </div>

          <p className="text-sm text-white/50 mt-8 text-center">
            Already with us?{" "}
            <Link to="/login" className="brand-text hover:underline" data-testid="goto-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
